import React, { useRef, useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { PlatformInfo } from '../pages/ProductMatrixNew';
import {
  Smartphone,
  Tablet,
  Monitor,
  Globe,
  Package
} from 'lucide-react';
import PlatformCard from './PlatformCard';
import { useProductMatrix } from '../contexts/ProductMatrixContext';
import { HeroPattern } from './HeroPattern';

interface HomePageV4Props {
  platforms: PlatformInfo[];
  onCountryClick?: (countryCode: string, countryName: string) => void;
  loading: boolean;
  theme?: 'light' | 'dark';
}

interface CountryCard {
  code: string;
  name: string;
  flag: string;
}

const HomePageV4: React.FC<HomePageV4Props> = ({
  platforms,
  onCountryClick,
  loading,
  theme = 'dark'
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { activeTab, setActiveTab } = useProductMatrix();

  // Country cards for onboarding tab
  const countryCards: CountryCard[] = [
    { code: 'US', name: 'United States', flag: '🇺🇸' },
    { code: 'Germany', name: 'Germany', flag: '🇩🇪' },
    // Add more countries as they become available in the database
  ];

  // Function to categorize platforms into tabs
  const categorizePlatform = (platformName: string): string => {
    const name = platformName.toLowerCase();

    if (name.includes('3pi') || name.includes('partner integrations') || name.includes('integrated payments') || name.includes('shift4 mobile')) {
      return '3PI';
    }
    if (name.includes('payments')) {
      return 'Shift4 Payments';
    }
    if (name.includes('skytab') || name.includes('lbm') || name.includes('solo')) {
      return 'Products';
    }

    // Default to Products for any other platforms
    return 'Products';
  };

  // Get hardware icons based on platform name
  const getHardwareIcons = (platform: PlatformInfo): { icon: React.ComponentType<any>, rotate?: boolean }[] => {
    const platformName = platform.name.toLowerCase();
    const icons: { icon: React.ComponentType<any>, rotate?: boolean }[] = [];

    if (platformName.includes('skytab') && !platformName.includes('venue')) {
      icons.push({ icon: Smartphone });
      icons.push({ icon: Tablet, rotate: true });
      icons.push({ icon: Monitor });
    } else if (platformName.includes('skytab') && platformName.includes('venue')) {
      icons.push({ icon: Smartphone });
      icons.push({ icon: Tablet, rotate: true });
    } else if (platformName.includes('shift4 solo') || platformName.includes('solo')) {
      icons.push({ icon: Smartphone });
    } else if (platformName.includes('shift4 mobile') || platformName.includes('mobile')) {
      icons.push({ icon: Smartphone });
    } else if (platformName.includes('online') || platformName.includes('payments')) {
      icons.push({ icon: Globe });
      icons.push({ icon: Monitor });
    } else if (platformName.includes('lbm')) {
      icons.push({ icon: Globe });
      icons.push({ icon: Monitor });
    } else if (platformName.includes('3pi') || platformName.includes('partner integrations')) {
      icons.push({ icon: Smartphone });
      icons.push({ icon: Tablet, rotate: true });
      icons.push({ icon: Monitor });
    }

    return icons;
  };

  // Get platform image
  const getPlatformImage = (platformName: string) => {
    const name = platformName.toLowerCase();

    if (name.includes('skytab') && !name.includes('venue')) return 'GlobalProductCatalog_SkyTab.svg'; // SkyTab
    if (name.includes('skytab') && name.includes('venue')) return 'GlobalProductCatalog_Venue.svg'; // SkyTab Venue
    if (name.includes('shift4 solo') || name.includes('solo')) return 'GlobalProductCatalog_Solo.svg'; // Shift4 Solo
    if (name.includes('shift4 mobile') || name.includes('mobile')) return 'GlobalProductCatalog_Mobile.svg'; // Shift4 Mobile
    if (name.includes('lbm')) return 'GlobalProductCatalog_LBM.svg'; // LBM 2.0 
    if (name.includes('3pi') || name.includes('integrated payments')) return 'GlobalProductCatalog_Solo.svg'; // Integrated Payments
    if (name.includes('payments')) return 'GlobalProductCatalog_Payments.svg'; // Shift4 Payments

    return 'GlobalProductCatalog_Mobile.svg'; // Default fallback
  };

  // Get platform image size class
  const getPlatformImageSize = (platformName: string) => {
    const name = platformName.toLowerCase();

    if (name.includes('skytab') && !name.includes('venue')) return 'w-[115%] h-[115%] opacity-100'; // SkyTab
    if (name.includes('skytab') && name.includes('venue')) return 'w-[110%] h-[110%] opacity-100'; // SkyTab Venue
    if (name.includes('shift4 solo') || name.includes('solo')) return 'w-[100%] h-[100%] opacity-100'; // Shift4 Solo
    if (name.includes('shift4 mobile') || name.includes('mobile')) return 'w-[100%] h-[100%]'; // Shift4 Mobile
    if (name.includes('lbm')) return 'w-[115%] h-[115%]'; // LBM 2.0
    if (name.includes('3pi') || name.includes('integrated payments')) return 'w-[100%] h-[100%]'; // Integrated Payments
    if (name.includes('payments')) return 'w-[110%] h-[110%]'; // Shift4 Payments

    return 'w-[80%] h-[80%]'; // Default fallback
  };

  // Function to get platform order
  const getPlatformOrder = (platformName: string): number => {
    const name = platformName.toLowerCase();
    if (name.includes('skytab') && !name.includes('venue')) return 0; // SkyTab
    if (name.includes('skytab') && name.includes('venue')) return 1; // SkyTab Venue
    if (name.includes('shift4 solo') || name.includes('solo')) return 2; // Shift4 Solo
    if (name.includes('shift4 mobile') || name.includes('mobile')) return 3; // Shift4 Mobile
    if (name.includes('lbm')) return 4; // LBM 2.0
    if (name.includes('3pi') || name.includes('integrated payments')) return 5; // Integrated Payments
    if (name.includes('payments')) return 6; // Shift4 Payments
    return 99;
  };

  // Map product category to tab ID
  const getTabFromCategory = (category: string): string => {
    const cat = category.toLowerCase();
    if (cat === 'solution') return 'product-modules';
    if (cat === 'hardware') return 'supported-hardware';
    if (cat === 'payment method') return 'supported-payments';
    if (cat === 'marketplace' || cat === '3pi') return 'partner-integrations';
    return 'product-modules'; // default
  };

  // Group platforms by category
  const productsPlatforms = platforms
    .filter(p => categorizePlatform(p.name) === 'Products')
    .sort((a, b) => getPlatformOrder(a.name) - getPlatformOrder(b.name));

  const integrationsPlatforms = platforms
    .filter(p => categorizePlatform(p.name) === '3PI')
    .sort((a, b) => getPlatformOrder(a.name) - getPlatformOrder(b.name));

  const paymentsPlatforms = platforms
    .filter(p => categorizePlatform(p.name) === 'Shift4 Payments')
    .sort((a, b) => getPlatformOrder(a.name) - getPlatformOrder(b.name));

  // Refs for scroll sections
  const productsRef = useRef<HTMLDivElement>(null);
  const integrationsRef = useRef<HTMLDivElement>(null);
  const paymentsRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isScrollingProgrammatically = useRef(false);

  // Scroll to section function
  const scrollToSection = (sectionId: string) => {
    const sectionMap: Record<string, React.RefObject<HTMLDivElement>> = {
      'products-section': productsRef,
      'integrations-section': integrationsRef,
      'payments-section': paymentsRef,
    };

    const ref = sectionMap[sectionId];
    if (ref?.current) {
      // Disable scroll spy during programmatic scroll
      isScrollingProgrammatically.current = true;

      ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });

      // Re-enable scroll spy after scroll animation completes (~600ms)
      setTimeout(() => {
        isScrollingProgrammatically.current = false;
      }, 600);
    }
  };

  // Handle hash-based scrolling on mount and when activeTab changes externally
  useEffect(() => {
    const hash = window.location.hash;
    if (hash === '#integrations') {
      setTimeout(() => scrollToSection('integrations-section'), 100);
    } else if (hash === '#payments') {
      setTimeout(() => scrollToSection('payments-section'), 100);
    }
  }, []);

  // Listen for scroll requests from Header
  useEffect(() => {
    const handleScrollRequest = ((event: CustomEvent) => {
      const { sectionId } = event.detail;
      scrollToSection(sectionId);
    }) as EventListener;

    window.addEventListener('scrollToSection', handleScrollRequest);
    return () => window.removeEventListener('scrollToSection', handleScrollRequest);
  }, []);

  // Scroll spy - highlight active tab based on scroll position
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    const handleScroll = () => {
      // Skip scroll spy if we're programmatically scrolling
      if (isScrollingProgrammatically.current) return;

      const sections = [
        { ref: productsRef, tabId: 'Products' },
        { ref: integrationsRef, tabId: '3PI' },
        { ref: paymentsRef, tabId: 'Shift4 Payments' }
      ];

      // Find which section is most visible
      let mostVisibleSection = sections[0];
      let maxVisibility = 0;

      sections.forEach(({ ref, tabId }) => {
        if (!ref.current) return;

        const rect = ref.current.getBoundingClientRect();
        const containerRect = scrollContainer.getBoundingClientRect();

        // Calculate how much of the section is visible
        const visibleTop = Math.max(rect.top, containerRect.top);
        const visibleBottom = Math.min(rect.bottom, containerRect.bottom);
        const visibleHeight = Math.max(0, visibleBottom - visibleTop);

        if (visibleHeight > maxVisibility) {
          maxVisibility = visibleHeight;
          mostVisibleSection = { ref, tabId };
        }
      });

      if (maxVisibility > 0) {
        setActiveTab(mostVisibleSection.tabId);
      }
    };

    scrollContainer.addEventListener('scroll', handleScroll);
    // Call once on mount to set initial state
    handleScroll();

    return () => scrollContainer.removeEventListener('scroll', handleScroll);
  }, [setActiveTab]);

  return (
    <>
      {/* Hero Pattern Background - Only on home page */}
      <HeroPattern />

      <div className="absolute inset-0">

        {/* Dot Grid Background Accent */}
      <div
        className="absolute top-0 right-0 w-72 h-72 opacity-0"
        style={{
          backgroundImage: theme === 'light'
            ? 'radial-gradient(circle, #9CA3AF 2px, transparent 2px)'
            : 'radial-gradient(circle, #6B7280 2px, transparent 2px)',
          backgroundSize: '40px 40px',
          backgroundPosition: '25px 25px'
        }}
      />

      {/* Blue Accent Circle */}
      <div
        className="absolute top-[-75px] right-48 w-40 h-40 rounded-full opacity-0"
        style={{
          background: 'linear-gradient(135deg, #1d4ed8, #1d4ed8)',
          filter: 'blur(0.5px)'
        }}
      />

      {/* Aqua Border Circle - Left side, partially clipped */}
      <div
        className="absolute top-1/2 left-[-150px] w-64 h-64 rounded-full opacity-0"
        style={{
          border: '40px solid #6BD8FF',
          background: 'transparent'
        }}
      />

      {/* Mobile Layout - Continuous Scroll Sections */}
      <div ref={scrollContainerRef} className="lg:hidden relative z-10 h-full overflow-y-auto">
        <div className="min-h-full flex items-start justify-start pt-8 px-6 lg:px-8">
          <div className="flex flex-col items-start justify-start space-y-6 text-left py-4 w-full max-w-5xl mx-auto">

            {loading ? (
              <div className="flex items-center justify-center py-12 w-full">
                <div className={`animate-spin rounded-full h-8 w-8 border-b-2 ${theme === 'light' ? 'border-gray-900' : 'border-white'}`}></div>
              </div>
            ) : (
              <>
                {/* Products Section */}
                <section ref={productsRef} id="products-section" className="w-full scroll-mt-32">
                  <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-6">
                    Products
                  </h2>
                  <div className="flex flex-wrap gap-4 mb-12">
                    {productsPlatforms.map((platform) => {
                      const isAvailable = platform.isAvailable;
                      const platformImage = getPlatformImage(platform.name);
                      const platformImageSize = getPlatformImageSize(platform.name);
                      const hardwareIcons = getHardwareIcons(platform);

                      return (
                        <PlatformCard
                          key={`products-${platform.name}`}
                          platform={platform}
                          isAvailable={isAvailable}
                          platformImage={platformImage}
                          platformImageSize={platformImageSize}
                          hardwareIcons={hardwareIcons}
                          onClick={() => navigate(`/platform/${encodeURIComponent(platform.name)}?from=Products`)}
                          theme={theme}
                        />
                      );
                    })}
                  </div>
                </section>

                {/* Integrations Section */}
                <section ref={integrationsRef} id="integrations-section" className="w-full scroll-mt-32">
                  <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-6">
                    Integrations
                  </h2>
                  <div className="flex flex-wrap gap-4 mb-12">
                    {integrationsPlatforms.map((platform) => {
                      const isAvailable = platform.isAvailable;
                      const platformImage = getPlatformImage(platform.name);
                      const platformImageSize = getPlatformImageSize(platform.name);
                      const hardwareIcons = getHardwareIcons(platform);

                      return (
                        <PlatformCard
                          key={`integrations-${platform.name}`}
                          platform={platform}
                          isAvailable={isAvailable}
                          platformImage={platformImage}
                          platformImageSize={platformImageSize}
                          hardwareIcons={hardwareIcons}
                          onClick={() => navigate(`/platform/${encodeURIComponent(platform.name)}?from=3PI`)}
                          theme={theme}
                        />
                      );
                    })}
                  </div>
                </section>

                {/* Payments Section */}
                <section ref={paymentsRef} id="payments-section" className="w-full scroll-mt-32">
                  <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-6">
                    Payments
                  </h2>
                  <div className="flex flex-wrap gap-4 mb-12">
                    {paymentsPlatforms.map((platform) => {
                      const isAvailable = platform.isAvailable;
                      const platformImage = getPlatformImage(platform.name);
                      const platformImageSize = getPlatformImageSize(platform.name);
                      const hardwareIcons = getHardwareIcons(platform);

                      return (
                        <PlatformCard
                          key={`payments-${platform.name}`}
                          platform={platform}
                          isAvailable={isAvailable}
                          platformImage={platformImage}
                          platformImageSize={platformImageSize}
                          hardwareIcons={hardwareIcons}
                          onClick={() => navigate(`/platform/${encodeURIComponent(platform.name)}?from=Shift4 Payments`)}
                          theme={theme}
                        />
                      );
                    })}
                  </div>
                </section>
              </>
            )}

            {/* Bottom spacer for mobile scrolling */}
            <div className="h-32 w-full"></div>
          </div>
        </div>
      </div>

      {/* Desktop Layout - Continuous Scroll Sections */}
      <div ref={scrollContainerRef} className="hidden lg:block relative z-10 w-full h-screen overflow-y-auto px-6 lg:px-8">
        <div className="max-w-5xl mx-auto text-left py-8 mt-20">

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className={`animate-spin rounded-full h-6 w-6 border-b-2 ${theme === 'light' ? 'border-gray-900' : 'border-white'}`}></div>
            </div>
          ) : (
            <>
              {/* Products Section */}
              <section ref={productsRef} id="products-section" className="scroll-mt-32 mb-16">
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
                  Products
                </h2>
                <p className="text-sm leading-relaxed text-gray-600 dark:text-gray-400 mb-6">
                  Explore our suite of point-of-sale systems and business management solutions.
                </p>
                <div className="flex flex-wrap gap-4">
                  {productsPlatforms.map((platform) => {
                    const isAvailable = platform.isAvailable;
                    const platformImage = getPlatformImage(platform.name);
                    const platformImageSize = getPlatformImageSize(platform.name);
                    const hardwareIcons = getHardwareIcons(platform);

                    return (
                      <PlatformCard
                        key={`products-${platform.name}`}
                        platform={platform}
                        isAvailable={isAvailable}
                        platformImage={platformImage}
                        platformImageSize={platformImageSize}
                        hardwareIcons={hardwareIcons}
                        onClick={() => navigate(`/platform/${encodeURIComponent(platform.name)}?from=Products`)}
                        theme={theme}
                      />
                    );
                  })}
                </div>
              </section>

              {/* Integrations Section */}
              <section ref={integrationsRef} id="integrations-section" className="scroll-mt-32 mb-16">
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
                  Integrations
                </h2>
                <p className="text-sm leading-relaxed text-gray-600 dark:text-gray-400 mb-6">
                  Explore our partner integrations and 3rd-party platforms that work seamlessly with Shift4 products.
                </p>
                <div className="flex flex-wrap gap-4">
                  {integrationsPlatforms.map((platform) => {
                    const isAvailable = platform.isAvailable;
                    const platformImage = getPlatformImage(platform.name);
                    const platformImageSize = getPlatformImageSize(platform.name);
                    const hardwareIcons = getHardwareIcons(platform);

                    return (
                      <PlatformCard
                        key={`integrations-${platform.name}`}
                        platform={platform}
                        isAvailable={isAvailable}
                        platformImage={platformImage}
                        platformImageSize={platformImageSize}
                        hardwareIcons={hardwareIcons}
                        onClick={() => navigate(`/platform/${encodeURIComponent(platform.name)}?from=3PI`)}
                        theme={theme}
                      />
                    );
                  })}
                </div>
              </section>

              {/* Payments Section */}
              <section ref={paymentsRef} id="payments-section" className="scroll-mt-32 mb-16">
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
                  Payments
                </h2>
                <p className="text-sm leading-relaxed text-gray-600 dark:text-gray-400 mb-6">
                  Discover our payment processing solutions and online payment platforms.
                </p>
                <div className="flex flex-wrap gap-4">
                  {paymentsPlatforms.map((platform) => {
                    const isAvailable = platform.isAvailable;
                    const platformImage = getPlatformImage(platform.name);
                    const platformImageSize = getPlatformImageSize(platform.name);
                    const hardwareIcons = getHardwareIcons(platform);

                    return (
                      <PlatformCard
                        key={`payments-${platform.name}`}
                        platform={platform}
                        isAvailable={isAvailable}
                        platformImage={platformImage}
                        platformImageSize={platformImageSize}
                        hardwareIcons={hardwareIcons}
                        onClick={() => navigate(`/platform/${encodeURIComponent(platform.name)}?from=Shift4 Payments`)}
                        theme={theme}
                      />
                    );
                  })}
                </div>
              </section>
            </>
          )}

          {/* Bottom spacer for scrolling */}
          <div className="h-32 w-full"></div>
        </div>
      </div>
      </div>
    </>
  );
};

export default HomePageV4;
